import { useState, useEffect, useCallback } from "react";
import { apiClient } from "~/services/api-client";

interface TimeseriesDataPoint {
  datetime: string;
  price: number;
  percent_move: number;
}

interface UseSecuritiesPerformanceProps {
  figis?: string[];
  timeframe?: "1M" | "3M" | "6M" | "1Y"; // Default to 1M
  autoFetch?: boolean;
}

interface UseSecuritiesPerformanceReturn {
  performanceData: Map<string, number | null>;
  timeseriesData: Map<string, TimeseriesDataPoint[]>;
  isLoading: boolean;
  error: string | null;
  fetchPerformance: () => Promise<void>;
}

// Calculate performance percentage from timeseries data
function calculatePerformance(
  timeseries: TimeseriesDataPoint[],
  daysBack: number
): number | null {
  if (!timeseries || timeseries.length === 0) {
    return null;
  }

  // Filter out entries with zero prices and sort by date descending (most recent first)
  const sorted = [...timeseries]
    .filter((point) => point.price > 0)
    .sort(
      (a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime()
    );

  // Need at least 2 valid data points to calculate performance
  if (sorted.length < 2) {
    return null;
  }

  // Get most recent non-zero price
  const currentPrice = sorted[0]?.price;

  // Get price from ~daysBack ago (approximately)
  // We'll try to get close to the target date, accounting for weekends/holidays
  const targetIndex = Math.min(daysBack, sorted.length - 1);
  const pastPrice = sorted[targetIndex]?.price;

  if (!currentPrice || !pastPrice || pastPrice === 0) {
    return null;
  }

  // Calculate percentage change
  const performancePercent = ((currentPrice - pastPrice) / pastPrice) * 100;
  return performancePercent;
}

// Get number of trading days for timeframe
function getDaysForTimeframe(timeframe: string): number {
  switch (timeframe) {
    case "1M":
      return 21; // ~1 month of trading days
    case "3M":
      return 63; // ~3 months of trading days
    case "6M":
      return 126; // ~6 months of trading days
    case "1Y":
      return 252; // ~1 year of trading days
    default:
      return 21;
  }
}

// Shared cache for performance data and timeseries
const performanceCache = new Map<
  string,
  {
    performanceData: Map<string, number | null>;
    timeseriesData: Map<string, TimeseriesDataPoint[]>;
    timestamp: number;
  }
>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Track in-flight requests to prevent duplicates
const inFlightRequests = new Map<
  string,
  Promise<{ performance: number | null; timeseries: TimeseriesDataPoint[] }>
>();

// Rate limiting: batch requests and add delays
const REQUEST_DELAY = 100; // ms between requests
const MAX_CONCURRENT = 3; // max concurrent requests

/**
 * Hook to fetch performance data for multiple securities
 * Fetches timeseries data and calculates performance over specified timeframe
 */
export function useSecuritiesPerformance({
  figis = [],
  timeframe = "1M",
  autoFetch = false,
}: UseSecuritiesPerformanceProps = {}): UseSecuritiesPerformanceReturn {
  const [performanceData, setPerformanceData] = useState<
    Map<string, number | null>
  >(new Map());
  const [timeseriesData, setTimeseriesData] = useState<
    Map<string, TimeseriesDataPoint[]>
  >(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to fetch a single FIGI's performance with deduplication
  const fetchSinglePerformance = useCallback(
    async (
      figi: string,
      daysBack: number
    ): Promise<{
      performance: number | null;
      timeseries: TimeseriesDataPoint[];
    }> => {
      const requestKey = `${figi}_${daysBack}`;

      // Check if request is already in flight
      const existingRequest = inFlightRequests.get(requestKey);
      if (existingRequest) {
        return existingRequest as Promise<{
          performance: number | null;
          timeseries: TimeseriesDataPoint[];
        }>;
      }

      // Create new request
      const requestPromise = (async () => {
        try {
          const params = new URLSearchParams({
            figi,
            interval: "MONTH",
          });

          const response = await apiClient.request({
            method: "GET",
            url: `/api/v1/timeseries/securities?${params}`,
          });

          if (response.error) {
            console.warn(
              `Failed to fetch timeseries for ${figi}:`,
              response.error
            );
            return { performance: null, timeseries: [] };
          }

          if (
            response.data &&
            Array.isArray(response.data) &&
            response.data.length > 0
          ) {
            const data = response.data[0] as {
              timeseries: TimeseriesDataPoint[];
            };
            const performance = calculatePerformance(data.timeseries, daysBack);
            return { performance, timeseries: data.timeseries };
          }

          return { performance: null, timeseries: [] };
        } catch (err) {
          console.error(`Error fetching performance for ${figi}:`, err);
          return { performance: null, timeseries: [] };
        } finally {
          // Remove from in-flight after completion
          inFlightRequests.delete(requestKey);
        }
      })();

      // Store in-flight request
      inFlightRequests.set(requestKey, requestPromise);
      return requestPromise;
    },
    []
  );

  const fetchPerformance = useCallback(async () => {
    if (!figis || figis.length === 0) {
      setError("No FIGIs provided");
      return;
    }

    // Create cache key from figis and timeframe
    const sortedFigis = [...figis].sort();
    const cacheKey = `${sortedFigis.join(",")}_${timeframe}`;

    // Check cache first
    const cached = performanceCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setPerformanceData(cached.performanceData);
      setTimeseriesData(cached.timeseriesData);
      return;
    }

    setIsLoading(true);
    setError(null);

    const perfResults = new Map<string, number | null>();
    const tsResults = new Map<string, TimeseriesDataPoint[]>();
    const daysBack = getDaysForTimeframe(timeframe);

    try {
      // Process FIGIs in small batches with delays to avoid rate limiting
      for (let i = 0; i < figis.length; i += MAX_CONCURRENT) {
        const batch = figis.slice(i, i + MAX_CONCURRENT);

        // Fetch batch in parallel
        const batchPromises = batch.map((figi) =>
          fetchSinglePerformance(figi, daysBack).then(
            ({ performance, timeseries }) => {
              perfResults.set(figi, performance);
              tsResults.set(figi, timeseries);
            }
          )
        );

        await Promise.all(batchPromises);

        // Add delay between batches (except for last batch)
        if (i + MAX_CONCURRENT < figis.length) {
          await new Promise((resolve) => setTimeout(resolve, REQUEST_DELAY));
        }

        // Update UI with partial results as we go
        setPerformanceData(new Map(perfResults));
        setTimeseriesData(new Map(tsResults));
      }

      // Cache the complete results (both performance and timeseries)
      performanceCache.set(cacheKey, {
        performanceData: perfResults,
        timeseriesData: tsResults,
        timestamp: Date.now(),
      });

      setPerformanceData(perfResults);
      setTimeseriesData(tsResults);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Unknown error occurred";
      console.error("Securities performance fetch error:", err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [figis, timeframe, fetchSinglePerformance]);

  // Clear data when FIGIs change
  useEffect(() => {
    setPerformanceData(new Map());
    setTimeseriesData(new Map());
    setError(null);
  }, [figis]);

  useEffect(() => {
    if (autoFetch && figis && figis.length > 0) {
      fetchPerformance();
    }
  }, [figis, autoFetch, fetchPerformance]);

  return {
    performanceData,
    timeseriesData,
    isLoading,
    error,
    fetchPerformance,
  };
}
